import { EImageVariant } from '@onesource/schemas';
import { axiosServiceInstance } from '@onesource/services';
import { readAndCompressImage } from 'browser-image-resizer';
import type { ProcessServerConfigFunction } from 'filepond';

export type TUploadMeta = {
  key: string;
  cdnUrls: Record<EImageVariant, string>;
  cdnSizes: Record<EImageVariant, number>;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
};

type TVariantConfig = {
  variant: EImageVariant;
  maxWidth: number | undefined;
  maxHeight: number | undefined;
  suffix: string;
};

const VARIANT_CONFIGS: TVariantConfig[] = [
  { variant: EImageVariant.w400, maxWidth: 400, maxHeight: 400, suffix: '-400x400' },
  { variant: EImageVariant.w800, maxWidth: 800, maxHeight: 800, suffix: '-800x800' },
  { variant: EImageVariant.w1070, maxWidth: 1070, maxHeight: 600, suffix: '-1070x600' },
  { variant: EImageVariant.w1200, maxWidth: 1200, maxHeight: 1200, suffix: '-1200x1200' },
  { variant: EImageVariant.w2400, maxWidth: 2400, maxHeight: 2400, suffix: '-2400x2400' },
  { variant: EImageVariant.original, maxWidth: undefined, maxHeight: undefined, suffix: '' },
];

const getPresignedUrl = async (
  key: string,
  contentType: string,
): Promise<{ uploadUrl: string; publicUrl: string | null }> => {
  const res = await axiosServiceInstance.get<{ uploadUrl: string; publicUrl: string | null }>('/upload/presigned-url', {
    params: { key, contentType },
  });
  return res.data;
};

const uploadToS3 = (
  uploadUrl: string,
  file: Blob,
  contentType: string,
  onProgress: (loaded: number, total: number) => void,
): Promise<void> =>
  new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.upload.onprogress = e => {
      if (e.lengthComputable) {
        onProgress(e.loaded, e.total);
      }
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error('Upload failed'));
      }
    };
    xhr.onerror = () => reject(new Error('Upload failed'));
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.send(file);
  });

const resizeImage = async (file: File, maxWidth: number, maxHeight: number): Promise<Blob> => {
  return readAndCompressImage(file, {
    quality: 0.85,
    maxWidth,
    maxHeight,
    mimeType: 'image/webp',
  });
};

export const buildProcessHandler =
  (enabledVariants: EImageVariant[] = Object.values(EImageVariant)): ProcessServerConfigFunction =>
  async (_fieldName, file, _metadata, load, error, progress, abort) => {
    const f = file as File;
    const isImage = f.type.startsWith('image/');
    const baseName = `${f.name.replace(/\.[^.]+$/, '')}-${Date.now()}`;
    const extension = f.name.includes('.') ? f.name.split('.').pop()! : ''; // used for key/url construction only

    let aborted = false;
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    const abortController = { abort: () => {} };

    try {
      let cdnUrls: Record<EImageVariant, string>;

      let cdnSizes: Record<EImageVariant, number>;

      if (isImage) {
        const activeVariants = VARIANT_CONFIGS.filter(v => enabledVariants.includes(v.variant));
        const urlEntries: Array<[EImageVariant, string]> = [];
        const sizeEntries: Array<[EImageVariant, number]> = [];
        const variantCount = activeVariants.length;

        for (let i = 0; i < activeVariants.length; i++) {
          if (aborted) {
            return;
          }
          const { variant, maxWidth, maxHeight, suffix } = activeVariants[i];

          const variantBlob =
            maxWidth && maxHeight ? await resizeImage(f, maxWidth, maxHeight) : new Blob([f], { type: f.type });

          const variantContentType = maxWidth ? 'image/webp' : f.type;
          const variantExt = maxWidth ? 'webp' : extension;
          const variantKey = `${baseName}${suffix}.${variantExt}`;

          const { uploadUrl, publicUrl } = await getPresignedUrl(variantKey, variantContentType);

          if (!publicUrl) {
            error('No public URL returned for variant');
            return;
          }

          await uploadToS3(uploadUrl, variantBlob, variantContentType, (loaded, total) => {
            // Weight progress across all variants
            const variantProgress = loaded / total;
            const overallProgress = (i + variantProgress) / variantCount;
            progress(true, Math.round(overallProgress * f.size), f.size);
          });

          urlEntries.push([variant, new URL(publicUrl).pathname]);
          sizeEntries.push([variant, variantBlob.size]);
        }

        cdnUrls = Object.fromEntries(urlEntries) as Record<EImageVariant, string>;
        cdnSizes = Object.fromEntries(sizeEntries) as Record<EImageVariant, number>;
      } else {
        // Non-image (PDF etc.) — upload original only
        const key = `${baseName}.${extension}`;
        const { uploadUrl, publicUrl } = await getPresignedUrl(key, f.type);

        if (!publicUrl) {
          error('No public URL returned');
          return;
        }

        await uploadToS3(uploadUrl, f, f.type, (loaded, total) => progress(true, loaded, total));

        cdnUrls = { [EImageVariant.original]: new URL(publicUrl).pathname } as Record<EImageVariant, string>;
        cdnSizes = { [EImageVariant.original]: f.size } as Record<EImageVariant, number>;
      }

      const meta: TUploadMeta = {
        key: baseName,
        cdnUrls,
        cdnSizes,
        originalName: f.name,
        mimeType: f.type,
        sizeBytes: f.size,
      };

      load(JSON.stringify(meta));
    } catch {
      if (!aborted) {
        error('Upload failed');
      }
    }

    return {
      abort: () => {
        aborted = true;
        abortController.abort();
        abort();
      },
    };
  };
